import React from 'react';
import { MapPin, ChevronDown, Navigation } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../ui/utils';

interface WellSelectorProps {
    selectedWell: any; // Type strictly if possible
    wells: any[];
    onSelectWell: (wellId: string) => void;
    distanceLabel: string;
    isDarkMode: boolean;
}

export function WellSelector({ selectedWell, wells, onSelectWell, distanceLabel, isDarkMode }: WellSelectorProps) {
    return (
        <div className={cn(
            "relative p-1 rounded-[1.5rem] border overflow-hidden",
            isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100 shadow-sm"
        )}>
            {/* Background Map Effect (Optional/Subtle) */}
            <div className={cn(
                "absolute inset-0 opacity-5 pointer-events-none",
                isDarkMode ? "bg-[url('/map-dark.png')]" : "bg-[url('/map-light.png')]"
            )} />

            <div className="relative p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                            isDarkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-600"
                        )}>
                            Active Well
                        </span>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Navigation className="w-3 h-3" />
                            {distanceLabel}
                        </div>
                    </div>
                    {/* Select / Display */}
                    <div className="relative">
                        <select
                            value={selectedWell?.id || ''}
                            onChange={(e) => onSelectWell(e.target.value)}
                            className={cn(
                                "w-full appearance-none bg-transparent text-lg font-bold truncate pr-8 focus:outline-none cursor-pointer",
                                isDarkMode ? "text-white" : "text-slate-900"
                            )}
                        >
                            {wells.map(w => (
                                <option key={w.id} value={w.id} className={isDarkMode ? "bg-slate-800" : "bg-white"}>
                                    {w.name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className={cn(
                            "absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none",
                            isDarkMode ? "text-slate-500" : "text-gray-400"
                        )} />
                    </div>
                    <div className={cn("text-xs truncate", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                        {selectedWell?.location?.name || selectedWell?.apiNumber || 'No location details'}
                    </div>
                </div>

                <div className={cn(
                    "w-12 h-12 rounded-2xl flex items-center justify-center ml-4 shrink-0 shadow-lg",
                    isDarkMode
                        ? "bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-blue-900/20"
                        : "bg-white text-blue-600 shadow-blue-100 border border-blue-50"
                )}>
                    <MapPin className="w-6 h-6" />
                </div>
            </div>
        </div>
    );
}
